import axios from 'axios'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  type KnowledgeFolderItem,
  useCreateKnowledgeFolder,
  useUpdateKnowledgeFolder,
} from '@/models/knowledgeFolder'

export type FolderDialogTarget =
  | { mode: 'create'; parent: string | null; parentPath: string }
  | { mode: 'edit'; folder: KnowledgeFolderItem }

type Props = {
  target: FolderDialogTarget | null
  onOpenChange: (open: boolean) => void
  onSaved?: (folder: KnowledgeFolderItem) => void
}

function errorMessage(err: unknown): string {
  const data = axios.isAxiosError(err) ? (err.response?.data as Record<string, string>) : undefined
  return data?.name || data?.detail || 'Could not save the folder.'
}

export function FolderDialog({ target, onOpenChange, onSaved }: Props) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const createMutation = useCreateKnowledgeFolder()
  const updateMutation = useUpdateKnowledgeFolder()

  useEffect(() => {
    if (!target) return
    setName(target.mode === 'edit' ? target.folder.name : '')
    setDescription(target.mode === 'edit' ? target.folder.description : '')
    setError(null)
  }, [target])

  const isPending = createMutation.isPending || updateMutation.isPending

  function handleSave() {
    const trimmed = name.trim()
    if (!trimmed || !target || isPending) return
    setError(null)
    const options = {
      onSuccess: (folder: KnowledgeFolderItem) => {
        onOpenChange(false)
        onSaved?.(folder)
      },
      onError: (err: unknown) => setError(errorMessage(err)),
    }
    if (target.mode === 'create') {
      createMutation.mutate({ name: trimmed, parent: target.parent, description }, options)
    } else {
      updateMutation.mutate({ id: target.folder.id, name: trimmed, description }, options)
    }
  }

  const title =
    target?.mode === 'edit'
      ? 'Edit folder'
      : target?.parentPath
        ? `New folder in ${target.parentPath}`
        : 'New folder'

  return (
    <Dialog open={Boolean(target)} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="folder-name">Name</Label>
            <Input
              id="folder-name"
              autoFocus
              value={name}
              placeholder="e.g. Carriers"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave()
              }}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="folder-description">Description</Label>
            <Textarea
              id="folder-description"
              rows={3}
              value={description}
              placeholder="What lives in here, and when an agent should read it."
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              Shown to the agents alongside the folder path, so they can pick the right branch before reading
              a document.
            </p>
          </div>
          {error && <p className="text-destructive text-sm">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || isPending}
            loading={isPending ? 'Saving…' : undefined}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
